<script setup lang="ts">
/**
 * H8 — Popular today carousel + load more
 * Figma RedisignHomePage `1:1848` / compact `1:1602`
 */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  fairScrollLeftTo,
  useFairDragScroll,
} from '@/composables/useFairDragScroll'
import { useAuthModal, useTokenStore } from '@/features/auth/store'
import { useVariantCompareStore } from '@/features/compare/store/useVariantCompareStore'
import { useDislikeProductMutation } from '@/mutations/product/useDislikeProductMutation'
import { useToggleFavoriteMutation } from '@/mutations/favorites/useToggleFavoriteMutation'
import { useRecommendationsCollectionsQuery } from '@/queries/recommendations'
import { COMPARISON_ROUTE, STYLE_SPACE_ROUTE } from '@/router/routes'
import { FairProductCardV2 } from '@/ui/patterns/FairProductCardV2'
import { useFairToast } from '@/ui/patterns/FairToast'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import { FairPaginationBar } from '@/ui/primitives/FairPaginationBar'
import { FairProgressSpinner } from '@/ui/primitives/FairProgressSpinner'
import {
  FAIR_VARIANT_COMPARE_MAX,
  type FairVariantCompareItem,
} from '@/ui/tokens/fair-variant-compare.data'
import {
  canFairHomeV2PopularLoadMore,
  FAIR_HOME_V2_POPULAR_COMPACT_CARD_WIDTH,
  FAIR_HOME_V2_POPULAR_COMPACT_GAP,
  FAIR_HOME_V2_POPULAR_DESKTOP_GAP,
  FAIR_HOME_V2_POPULAR_DESKTOP_PAGE_SIZE,
  FAIR_HOME_V2_POPULAR_INITIAL_LIMIT,
  FAIR_HOME_V2_POPULAR_LOAD_MORE_STEP,
  getFairHomeV2PopularPageCount,
  mapFairHomeV2PopularAudience,
  normalizeFairHomeV2PopularPage,
  resolveFairHomeV2PopularCards,
  resolveFairHomeV2PopularScrollPage,
  type FairHomeV2PopularCard,
} from './fair-home-v2-popular.data'
import './fair-home-v2-popular.css'

const props = withDefaults(
  defineProps<{
    compact?: boolean
    audience?: string
  }>(),
  {
    compact: false,
    audience: 'women',
  },
)

const { t } = useI18n()
const router = useRouter()
const { show: showToast } = useFairToast()
const tokenStore = useTokenStore()
const authModal = useAuthModal()
const compareStore = useVariantCompareStore()
const dislikeMutation = useDislikeProductMutation()
const favoriteMutation = useToggleFavoriteMutation()

const page = ref(0)
const limit = ref(FAIR_HOME_V2_POPULAR_INITIAL_LIMIT)
const viewportRef = ref<HTMLElement | null>(null)
const cardPages = reactive<Record<number, number>>({})
const removedIds = ref<Set<number>>(new Set())
const localFavorites = reactive<Record<number, boolean>>({})

useFairDragScroll(viewportRef)

let rowScrollTarget: HTMLElement | null = null

const apiAudience = computed(() => mapFairHomeV2PopularAudience(props.audience))

const collectionsQuery = useRecommendationsCollectionsQuery(limit, apiAudience)

const resolvedCards = computed(() =>
  resolveFairHomeV2PopularCards(collectionsQuery.data.value?.items),
)

const products = computed(() =>
  resolvedCards.value
    .filter((item) => !removedIds.value.has(item.id))
    .map((item) => ({
      ...item,
      isFavorite: localFavorites[item.id] ?? item.isFavorite,
    })),
)

const isLoading = computed(
  () => collectionsQuery.isLoading.value && collectionsQuery.data.value == null,
)

const isLoadingMore = computed(
  () =>
    collectionsQuery.isLoading.value && collectionsQuery.data.value != null,
)

const cardSize = computed(() => (props.compact ? 'mobile' : 'default'))

const pageCount = computed(() =>
  getFairHomeV2PopularPageCount(products.value.length, props.compact),
)

const safePage = computed(() =>
  normalizeFairHomeV2PopularPage(page.value, pageCount.value),
)

const showNav = computed(
  () =>
    !props.compact
    && products.value.length > FAIR_HOME_V2_POPULAR_DESKTOP_PAGE_SIZE,
)

const canPrev = computed(() => safePage.value > 0)
const canNext = computed(() => safePage.value < pageCount.value - 1)

const showPager = computed(
  () =>
    props.compact
    && products.value.length > 1
    && pageCount.value > 1,
)

const showLoadMore = computed(() =>
  canFairHomeV2PopularLoadMore(
    resolvedCards.value.length,
    limit.value,
  ),
)

const sectionStyle = computed(() => {
  if (!props.compact) {
    return {
      '--fair-h2-popular-gap': `${FAIR_HOME_V2_POPULAR_DESKTOP_GAP}px`,
      '--fair-h2-popular-page-size': String(
        FAIR_HOME_V2_POPULAR_DESKTOP_PAGE_SIZE,
      ),
    }
  }
  return {
    '--fair-h2-popular-gap': `${FAIR_HOME_V2_POPULAR_COMPACT_GAP}px`,
    '--fair-h2-popular-card-width': `${FAIR_HOME_V2_POPULAR_COMPACT_CARD_WIDTH}px`,
  }
})

const rootClass = computed(() => ({
  'fair-home-v2-popular': true,
  'fair-home-v2-popular--compact': props.compact,
  'fair-home-v2-popular--desktop': !props.compact,
}))

watch(
  () => products.value.length,
  () => {
    page.value = normalizeFairHomeV2PopularPage(page.value, pageCount.value)
    void nextTick(() => {
      scrollToPage(page.value, 'auto')
      syncScrollPage()
    })
  },
)

watch(
  () => props.compact,
  () => {
    page.value = 0
    void nextTick(() => {
      scrollToPage(0, 'auto')
    })
  },
)

watch(
  () => props.audience,
  () => {
    limit.value = FAIR_HOME_V2_POPULAR_INITIAL_LIMIT
    removedIds.value = new Set()
    Object.keys(localFavorites).forEach((key) => {
      delete localFavorites[Number(key)]
    })
    page.value = 0
    void collectionsQuery.refresh(true)
  },
)

function compactCardStep(): number {
  return FAIR_HOME_V2_POPULAR_COMPACT_CARD_WIDTH + FAIR_HOME_V2_POPULAR_COMPACT_GAP
}

function pageScrollStep(): number {
  if (props.compact) return compactCardStep()
  return viewportRef.value?.clientWidth ?? 0
}

function scrollToPage(index: number, behavior: ScrollBehavior = 'smooth') {
  const el = viewportRef.value
  if (!el) return
  const step = pageScrollStep()
  if (step <= 0) return
  const next = normalizeFairHomeV2PopularPage(index, pageCount.value)
  page.value = next
  const left = next * step
  if (behavior === 'auto') {
    el.scrollLeft = left
    return
  }
  fairScrollLeftTo(el, left, { durationMs: 720 })
}

function unbindRowScroll() {
  if (!rowScrollTarget) return
  rowScrollTarget.removeEventListener('scroll', handleScroll)
  rowScrollTarget = null
}

function bindRowScroll(el: HTMLElement | null) {
  unbindRowScroll()
  if (!el) return
  el.addEventListener('scroll', handleScroll, { passive: true })
  rowScrollTarget = el
  syncScrollPage()
}

function syncScrollPage() {
  const el = viewportRef.value
  if (!el) return
  const step = pageScrollStep()
  if (step <= 0) return
  const total = props.compact
    ? products.value.length
    : pageCount.value
  page.value = resolveFairHomeV2PopularScrollPage(el.scrollLeft, step, total)
}

function getCardPage(product: FairHomeV2PopularCard): number {
  return cardPages[product.id] ?? 0
}

function setCardPage(productId: number, next: number) {
  cardPages[productId] = next
}

function productTo(product: FairHomeV2PopularCard) {
  return {
    name: 'product' as const,
    params: {
      productId: String(product.id),
      productVariantId: String(product.variantId),
    },
  }
}

function toCompareItem(product: FairHomeV2PopularCard): FairVariantCompareItem {
  return {
    productId: product.id,
    variantId: product.variantId,
    brand: product.brand,
    title: product.title,
    price: product.price,
    imageUrl: product.images[0],
  }
}

function handlePrev() {
  if (!canPrev.value) return
  scrollToPage(safePage.value - 1)
}

function handleNext() {
  if (!canNext.value) return
  scrollToPage(safePage.value + 1)
}

function handlePager(index: number) {
  scrollToPage(index)
}

function handleScroll() {
  syncScrollPage()
}

function handleLoadMore() {
  if (!showLoadMore.value || isLoadingMore.value) return
  limit.value += FAIR_HOME_V2_POPULAR_LOAD_MORE_STEP
}

function handleCardClick(product: FairHomeV2PopularCard) {
  void router.push(productTo(product))
}

async function handleDislike(product: FairHomeV2PopularCard) {
  const restore = () => {
    const next = new Set(removedIds.value)
    next.delete(product.id)
    removedIds.value = next
  }

  try {
    await dislikeMutation.mutateAsync({ variantId: product.variantId })
    const next = new Set(removedIds.value)
    next.add(product.id)
    removedIds.value = next
    showToast({
      message: t('fair_product.toast_dislike'),
      linkLabel: t('fair_product.toast_undo'),
      onLinkClick: restore,
    })
  } catch {
    showToast({
      tone: 'warning',
      message: t('fair_product.toast_save_fail'),
    })
  }
}

async function handleFavorite(product: FairHomeV2PopularCard) {
  const current = Boolean(product.isFavorite)
  localFavorites[product.id] = !current
  try {
    await favoriteMutation.mutateAsync({
      variantId: product.variantId,
      isFavorite: current,
    })
  } catch {
    localFavorites[product.id] = current
  }
}

function handleSave(product: FairHomeV2PopularCard) {
  if (!tokenStore.isAuthenticated()) {
    authModal.showAuthModal('signInSignUpWrapper')
  }
}

function handleCompare(product: FairHomeV2PopularCard) {
  const result = compareStore.toggle(toCompareItem(product))
  if (result === 'added') {
    showToast({
      message: t('fair_product.toast_compare_added'),
      linkLabel: t('fair_product.toast_open'),
      onLinkClick: () => {
        void router.push(COMPARISON_ROUTE)
      },
    })
    return
  }
  if (result === 'removed') {
    showToast({ message: t('fair_compare.removed') })
    return
  }
  showToast({
    tone: 'warning',
    message: t('fair_compare.limit', { max: FAIR_VARIANT_COMPARE_MAX }),
  })
}

function handleStyle(product: FairHomeV2PopularCard) {
  void router.push({
    path: STYLE_SPACE_ROUTE,
    query: {
      productId: String(product.id),
      variantId: String(product.variantId),
    },
  })
}

watch(viewportRef, (el) => {
  bindRowScroll(el)
})

onMounted(() => {
  bindRowScroll(viewportRef.value)
})

onBeforeUnmount(() => {
  unbindRowScroll()
})
</script>

<template>
  <section
    :class="rootClass"
    :style="sectionStyle"
    aria-label="popular today"
  >
    <header class="fair-home-v2-popular__heading">
      <div class="fair-home-v2-popular__heading-text">
        <h2 class="fair-home-v2-popular__title">
          {{ t('fair_home_v2.popular.title') }}
        </h2>
      </div>
      <div
        v-if="showNav"
        class="fair-home-v2-popular__nav"
      >
        <FairIconButton
          icon="arrow-back"
          size="small"
          :disabled="!canPrev"
          :aria-label="t('fair_home_v2.popular.carousel_prev')"
          @click="handlePrev"
        />
        <FairIconButton
          icon="arrow-forward"
          size="small"
          :disabled="!canNext"
          :aria-label="t('fair_home_v2.popular.carousel_next')"
          @click="handleNext"
        />
      </div>
    </header>

    <div
      v-if="isLoading"
      class="fair-home-v2-popular__loading"
      role="status"
    >
      <FairProgressSpinner />
    </div>

    <div
      v-else-if="products.length"
      ref="viewportRef"
      class="fair-home-v2-popular__row"
      @scroll.passive="handleScroll"
    >
      <FairProductCardV2
        v-for="(product, index) in products"
        :key="product.id"
        class="fair-home-v2-popular__card"
        :size="cardSize"
        :brand="product.brand"
        :title="product.title"
        :price="product.price"
        :from-label="t('fair_product.price_from_short')"
        :images="product.images"
        :colors="product.colors ?? []"
        :colors-total="product.colorsTotal"
        :page="getCardPage(product)"
        :favorite="Boolean(product.isFavorite)"
        :to="productTo(product)"
        :priority="index < 2"
        @update:page="setCardPage(product.id, $event)"
        @click="handleCardClick(product)"
        @dislike="handleDislike(product)"
        @favorite="handleFavorite(product)"
        @save="handleSave(product)"
        @compare="handleCompare(product)"
        @style="handleStyle(product)"
      />
    </div>

    <p
      v-else
      class="fair-home-v2-popular__empty"
    >
      {{ t('fair_home_v2.popular.empty') }}
    </p>

    <div
      v-if="showPager"
      class="fair-home-v2-popular__pager"
    >
      <FairPaginationBar
        :count="pageCount"
        :model-value="safePage"
        @update:model-value="handlePager"
      />
    </div>

    <div
      v-if="showLoadMore"
      class="fair-home-v2-popular__load-more"
    >
      <FairButton
        class="fair-home-v2-popular__load-more-button"
        variant="primary"
        size="m"
        :rounded="false"
        :label="t('fair_home_v2.popular.show_more')"
        :disabled="isLoadingMore"
        @click="handleLoadMore"
      />
    </div>
  </section>
</template>
